<template>
  <div class="cp-employment-charts">
    <div v-if="isLoading" class="cp-employment-charts__loading">
      <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
    </div>

    <div v-else-if="loadError" class="cp-employment-charts__error">
      <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
      <p>{{ loadError }}</p>
    </div>

    <template v-else>
      <div class="cp-employment-kpi">
        <div class="cp-employment-kpi__icon">
          <font-awesome-icon :icon="['fas', 'briefcase']" />
        </div>
        <div>
          <p class="cp-employment-kpi__value">{{ formatNumber(stats.total_employed) }}</p>
          <p class="cp-employment-kpi__label">Alumni employed at this partner</p>
        </div>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section
          v-for="chart in chartConfigs"
          :key="chart.key"
          class="cp-employment-chart-card"
        >
          <h3 class="cp-employment-chart-card__title">{{ chart.title }}</h3>
          <p class="cp-employment-chart-card__subtitle">{{ chart.subtitle }}</p>
          <apexchart
            v-if="chart.hasData"
            type="bar"
            :height="chart.height"
            :options="chart.options"
            :series="chart.series"
            @dataPointSelection="(event, chartContext, config) => onChartBarSelect(chart.key, config)"
          />
          <div v-else class="cp-employment-empty">
            <font-awesome-icon :icon="chart.emptyIcon" class="cp-employment-empty__icon" />
            <p class="cp-employment-empty__title">{{ chart.emptyTitle }}</p>
            <p class="cp-employment-empty__text">{{ chart.emptyText }}</p>
          </div>
        </section>
      </div>
    </template>

    <partner-employment-drilldown-modal
      :visible="drilldownVisible"
      :title="drilldownTitle"
      :file-name="drilldownFileName"
      :partner-token="partnerToken"
      :breakdown="drilldownBreakdown"
      :label="drilldownLabel"
      @close="closeDrilldown"
    />
  </div>
</template>

<script>
import gql from "graphql-tag";
import PartnerEmploymentDrilldownModal from "../components/PartnerEmploymentDrilldownModal.vue";

var CHART_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#db2777",
  "#4f46e5",
];

export default {
  name: "PartnerEmploymentCharts",
  components: {
    PartnerEmploymentDrilldownModal: PartnerEmploymentDrilldownModal,
  },
  props: {
    partnerToken: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      loadError: null,
      stats: {
        total_employed: 0,
        by_entity: [],
        by_campus: [],
        by_academic_year: [],
        by_program: [],
        by_position: [],
      },
      drilldownVisible: false,
      drilldownBreakdown: "",
      drilldownLabel: "",
      drilldownTitle: "",
      drilldownFileName: "Partner_Employment_Drilldown",
    };
  },
  computed: {
    chartConfigs() {
      var self = this;
      return [
        {
          key: "entity",
          title: "Employment by entity",
          subtitle: "Alumni linked to this partner grouped by institution",
          rows: this.stats.by_entity || [],
          emptyIcon: ["fas", "sitemap"],
          emptyTitle: "No entity data",
          emptyText: "Alumni employment records linked to this partner will appear here once entity data is available.",
        },
        {
          key: "campus",
          title: "Employment by campus",
          subtitle: "Distribution across campuses",
          rows: this.stats.by_campus || [],
          emptyIcon: ["fas", "university"],
          emptyTitle: "No campus data",
          emptyText: "Campus breakdown will show when alumni employed at this partner have campus information.",
        },
        {
          key: "academic_year",
          title: "Employment by academic year",
          subtitle: "Graduating cohorts placed with this partner",
          rows: this.stats.by_academic_year || [],
          emptyIcon: ["fas", "calendar-alt"],
          emptyTitle: "No academic year data",
          emptyText: "Academic year trends will appear once linked alumni records include graduation year details.",
        },
        {
          key: "program",
          title: "Employment by program",
          subtitle: "Programs represented among employed alumni",
          rows: this.stats.by_program || [],
          emptyIcon: ["fas", "graduation-cap"],
          emptyTitle: "No program data",
          emptyText: "Program distribution will display when alumni linked to this partner have program information.",
        },
        {
          key: "position",
          title: "Employment by position",
          subtitle: "Job positions held by alumni at this partner",
          rows: this.stats.by_position || [],
          emptyIcon: ["fas", "id-badge"],
          emptyTitle: "No position data",
          emptyText: "Position breakdown will appear when linked alumni have an employment position recorded.",
        },
      ].map(function (config) {
        var rows = config.rows || [];
        var hasData = rows.length > 0;
        var isHorizontal = config.key === "program" || config.key === "position";
        var height = self.getChartHeight(rows.length, isHorizontal);
        return {
          key: config.key,
          title: config.title,
          subtitle: config.subtitle,
          emptyIcon: config.emptyIcon,
          emptyTitle: config.emptyTitle,
          emptyText: config.emptyText,
          hasData: hasData,
          height: height,
          series: hasData
            ? [
                {
                  name: "Alumni",
                  data: rows.map(function (row) {
                    return row.count;
                  }),
                },
              ]
            : [],
          options: self.buildChartOptions(rows, isHorizontal),
        };
      });
    },
  },
  watch: {
    partnerToken: {
      immediate: true,
      handler: function () {
        this.fetchStats();
      },
    },
  },
  methods: {
    formatNumber(value) {
      var num = Number(value);
      if (isNaN(num)) {
        return "0";
      }
      return num.toLocaleString();
    },
    getChartHeight(count, isProgram) {
      if (!count) {
        return 280;
      }
      if (isProgram) {
        if (count <= 3) {
          return 260;
        }
        if (count <= 6) {
          return 320;
        }
        return Math.min(520, 240 + count * 28);
      }
      if (count <= 3) {
        return 260;
      }
      if (count <= 6) {
        return 300;
      }
      return 340;
    },
    buildChartOptions(rows, horizontal) {
      var labels = (rows || []).map(function (row) {
        return row.label || "Unknown";
      });
      var baseOptions = {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        colors: CHART_COLORS,
        plotOptions: {
          bar: {
            borderRadius: 6,
            borderRadiusApplication: "end",
            distributed: true,
            dataLabels: {
              position: "center",
            },
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#ffffff"],
          },
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 4,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " alumn" + (val === 1 ? "us" : "i");
            },
          },
        },
      };

      if (horizontal) {
        baseOptions.plotOptions.bar.horizontal = true;
        baseOptions.plotOptions.bar.barHeight = labels.length <= 4 ? "55%" : "70%";
        baseOptions.xaxis = {
          categories: labels,
          labels: {
            style: {
              fontSize: "11px",
            },
          },
        };
        baseOptions.yaxis = {
          title: {
            text: "Alumni count",
            style: {
              fontSize: "12px",
              fontWeight: 600,
            },
          },
          min: 0,
          forceNiceScale: true,
        };
      } else {
        baseOptions.plotOptions.bar.columnWidth = "55%";
        baseOptions.xaxis = {
          categories: labels,
          labels: {
            rotate: labels.length > 5 ? -35 : 0,
            style: {
              fontSize: "11px",
            },
          },
        };
        baseOptions.yaxis = {
          title: {
            text: "Alumni count",
            style: {
              fontSize: "12px",
              fontWeight: 600,
            },
          },
          min: 0,
          forceNiceScale: true,
        };
      }

      return baseOptions;
    },
    getChartRows(chartKey) {
      if (chartKey === "entity") {
        return this.stats.by_entity || [];
      }
      if (chartKey === "campus") {
        return this.stats.by_campus || [];
      }
      if (chartKey === "academic_year") {
        return this.stats.by_academic_year || [];
      }
      if (chartKey === "program") {
        return this.stats.by_program || [];
      }
      if (chartKey === "position") {
        return this.stats.by_position || [];
      }
      return [];
    },
    getBreakdownTitlePrefix(chartKey) {
      if (chartKey === "entity") {
        return "Entity";
      }
      if (chartKey === "campus") {
        return "Campus";
      }
      if (chartKey === "academic_year") {
        return "Academic year";
      }
      if (chartKey === "program") {
        return "Program";
      }
      if (chartKey === "position") {
        return "Position";
      }
      return "Selection";
    },
    onChartBarSelect(chartKey, config) {
      var idx =
        config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
          ? config.dataPointIndex
          : null;
      if (idx === null || idx < 0) {
        return;
      }

      var rows = this.getChartRows(chartKey);
      if (!rows || idx >= rows.length) {
        return;
      }

      var row = rows[idx];
      var label = row && row.label ? row.label : "Unknown";
      var prefix = this.getBreakdownTitlePrefix(chartKey);

      this.drilldownBreakdown = chartKey;
      this.drilldownLabel = label;
      this.drilldownTitle = prefix + ": " + label;
      this.drilldownFileName = "Partner_Employment_" + chartKey + "_" + String(label).replace(/[^\w-]+/g, "_");
      this.drilldownVisible = true;
    },
    closeDrilldown() {
      this.drilldownVisible = false;
      this.drilldownBreakdown = "";
      this.drilldownLabel = "";
      this.drilldownTitle = "";
    },
    async fetchStats() {
      if (!this.partnerToken) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.loadError = null;

      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetCurrentPartnerEmploymentStats($token: String!) {
              getCurrentPartnerEmploymentStats(token: $token) {
                error
                message
                data {
                  total_employed
                  by_entity {
                    label
                    count
                  }
                  by_campus {
                    label
                    count
                  }
                  by_academic_year {
                    label
                    count
                  }
                  by_program {
                    label
                    count
                  }
                  by_position {
                    label
                    count
                  }
                }
              }
            }
          `,
          variables: { token: this.partnerToken },
          fetchPolicy: "network-only",
        });

        var payload =
          result.data && result.data.getCurrentPartnerEmploymentStats
            ? result.data.getCurrentPartnerEmploymentStats
            : null;

        if (!payload) {
          this.loadError = "Failed to load employment statistics.";
          return;
        }

        if (payload.error) {
          this.loadError = payload.message || "Failed to load employment statistics.";
          return;
        }

        this.stats = payload.data || {
          total_employed: 0,
          by_entity: [],
          by_campus: [],
          by_academic_year: [],
          by_program: [],
          by_position: [],
        };
      } catch (err) {
        this.loadError =
          err && err.message ? err.message : "Failed to load employment statistics.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cp-employment-charts__loading,
.cp-employment-charts__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  color: #64748b;
  text-align: center;
  gap: 0.5rem;
}

.cp-employment-kpi {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.875rem;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #dbeafe;
}

.cp-employment-kpi__icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: #2563eb;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.cp-employment-kpi__value {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.cp-employment-kpi__label {
  margin: 0.2rem 0 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.cp-employment-chart-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

.cp-employment-chart-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.cp-employment-chart-card__subtitle {
  margin: 0.25rem 0 1rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.cp-employment-chart-card :deep(.apexcharts-bar-area) {
  cursor: pointer;
}

.cp-employment-empty {
  min-height: 16rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem 1rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border: 1px dashed #cbd5e1;
}

.cp-employment-empty__icon {
  font-size: 2rem;
  color: #93c5fd;
  margin-bottom: 0.75rem;
}

.cp-employment-empty__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.35rem;
}

.cp-employment-empty__text {
  font-size: 0.8125rem;
  color: #94a3b8;
  max-width: 22rem;
  line-height: 1.45;
  margin: 0;
}
</style>
